import { store } from "@/store/index";
import { ENDPOINTS } from "@/app/app-config";
import { ROUTES } from "@/app/app-constants";
import { getSecurityToken } from "@/federation/getSecurityToken";
import { useHostSelector } from "@/federation/useHostSelector";
import { pendoService } from "@/services/pendo/Pendo";
import rumService, { RUM_EVENT_TYPES, type RumEventType } from "@/utils/awsRUM/awsRUM";

interface AuthRealmDomain {
  realm: string;
  domain: string;
}

const getServiceUrl = (service: string): string => {
  const { authInfo } = store.getState() as { authInfo?: Partial<AuthRealmDomain> };
  const realm = authInfo?.realm;
  const domain = authInfo?.domain;
  if (!realm || !domain) {
    throw new Error("relayYardTrackerHostServices: authInfo realm/domain not initialized");
  }
  return ENDPOINTS[realm][domain][service];
};

/**
 * The host's conformance to the RelayYardTracker package's HostServices
 * interface (consumer-defines-the-interface DI). Passed to
 * <RelayYardTrackerApp hostServices={...}> at the lazy route. Module singleton
 * so the prop is referentially stable.
 *
 * `useFeatureFlag` is a HOOK, not a snapshot getter: RYT gates two tabs on host
 * flags that load async, so it must re-render the child when a flag resolves.
 * It reads through `useHostSelector` (the federation seam's reactive read) +
 * the `featureFlags` projection on HostPublicState. The child calls it during
 * render (replacing its former `useSelector(s => s.featureFlags)`), so
 * rules-of-hooks hold. A call-time getter here would capture the pre-load
 * `false` and the tabs would never appear.
 */
export const relayYardTrackerHostServices = {
  getSecurityToken,
  getYard360Endpoint: () => getServiceUrl("Yard360Service"),
  getYardConsoleEndpoint: () => getServiceUrl("YardConsoleService"),
  useFeatureFlag: (flagKey: string): boolean =>
    useHostSelector((state) => state.featureFlags[flagKey] ?? false),
  routes: { relayYardTracker: ROUTES.RYT },
  telemetry: {
    trackPendoEvent: (eventName: string, data?: Record<string, unknown>) =>
      pendoService.trackEvent(eventName, data),
    trackRumCustomEvent: (event: {
      type: string;
      message: string;
      data?: Record<string, unknown>;
    }) =>
      rumService.customEvent({
        type: (Object.values(RUM_EVENT_TYPES) as string[]).includes(event.type)
          ? (event.type as RumEventType)
          : RUM_EVENT_TYPES.USER_ACTION,
        message: event.message,
        data: event.data,
      }),
  },
};
